/**
 * apps/web/src/api/client.ts — the ONLY place `fetch` is called.
 *
 * Spec: 12-api-contract.md §1.6 (there is no success envelope — the resource
 * IS the body), §1.7 (one error envelope, always), §1.10 (headers),
 * §3.1 (the access token lives in memory; the refresh token is an HttpOnly
 * cookie scoped to /api/v1/auth and is rotated on every refresh).
 *
 * Screens render `error.meta` inline. They must NEVER parse `error.message` —
 * it is human-readable English that may change between releases.
 */
import type { ApiErrorBody, ErrorCode, FieldError } from '@cropmate/shared';

export const API_BASE = '/api/v1';

/* ══════════════════════════ 1. The typed error ═══════════════════════════ */

/**
 * A non-2xx response from `/api/v1/**`. Carries the whole error envelope, so a
 * screen can branch on `code` and render `meta` without touching `message`.
 */
export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly requestId: string;
  readonly timestamp: string;
  readonly path: string;
  readonly details: FieldError[] | undefined;
  readonly meta: Record<string, unknown> | undefined;

  constructor(body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.code = body.code;
    this.status = body.status;
    this.requestId = body.requestId;
    this.timestamp = body.timestamp;
    this.path = body.path;
    this.details = body.details;
    this.meta = body.meta;
  }

  /** Read one key out of `meta` without an `any` at the call site. */
  metaValue<T = unknown>(key: string): T | undefined {
    return this.meta ? (this.meta[key] as T | undefined) : undefined;
  }

  /** The field error for a form control, if the server sent one. */
  fieldError(field: string): FieldError | undefined {
    return this.details?.find((d) => d.field === field);
  }

  is(...codes: ErrorCode[]): boolean {
    return codes.includes(this.code);
  }
}

/** The server could not be reached at all. Not an HTTP status. */
export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super('Cannot reach the server. Check the network and try again.');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

/* ══════════════════════ 2. In-memory token storage ═══════════════════════ */

/**
 * The access token is held in a module closure and NEVER in localStorage or
 * sessionStorage (12-api-contract.md §3.1). Losing it on a hard refresh is
 * intended: the HttpOnly `cm_rt` cookie re-mints one through A2.
 */
let accessToken: string | null = null;

/** The header `CompanyGuard` reads. One spelling, in one place. */
export const COMPANY_HEADER = 'X-Company-Id';

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

/**
 * The company every request acts as, sent as `X-Company-Id`.
 *
 * Held here rather than imported from `lib/auth.ts`, which would be a cycle —
 * `auth.ts` already imports `setAccessToken` from this module. It mirrors the
 * token's own accessor pair for the same reason: the fetch wrapper is the only
 * place a header is set, so the value has to live where the wrapper can see it.
 *
 * This is an ASSERTION, never an authority. `CompanyGuard` re-validates it
 * against `company_membership` on every request and answers 403 `E_NOT_A_MEMBER`
 * for anything the caller does not belong to, so a tampered value buys nothing.
 * Omitting it is safe too: the server falls back to the caller's default
 * membership, which is what makes a single-company account work without the SPA
 * ever having to know the system is multi-company.
 */
let companyId: string | null = null;

export function getCompanyId(): string | null {
  return companyId;
}

export function setCompanyId(id: string | null): void {
  companyId = id;
}

/* ═══════════════ 3. Refresh rotation and the 401 handler ═════════════════ */

/**
 * Receives the 401 that STARTED the recovery (not the refresh's own failure), so
 * the handler can tell the user why they are back on Sign in: `meta.reason =
 * 'ACCESS_CHANGED'` means an Administrator changed their roles, a role they
 * hold, or disabled them (`jwt.strategy.ts`, UAT ACC-025 / ACC-030); anything
 * else is a session that simply ended.
 */
type UnauthorizedHandler = (cause: ApiError) => void;
let onUnauthorized: UnauthorizedHandler = () => {};

/**
 * Registered once by the auth module. Called after a refresh attempt has
 * definitively failed, so the app can clear state and land on /login.
 */
export function setUnauthorizedHandler(fn: UnauthorizedHandler): void {
  onUnauthorized = fn;
}

/** Single-flight: a burst of parallel 401s must produce ONE refresh call. */
let refreshInFlight: Promise<boolean> | null = null;

interface RefreshResponse {
  accessToken: string;
  tokenType: 'Bearer';
  expiresIn: number;
}

/**
 * A2 `POST /auth/refresh`. The rotated refresh token comes back as a
 * `Set-Cookie`; presenting a used one revokes the whole session family, which
 * is exactly why this must be single-flight.
 */
export async function refreshAccessToken(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) {
        accessToken = null;
        return false;
      }
      const body = (await res.json()) as RefreshResponse;
      accessToken = body.accessToken;
      return true;
    } catch {
      accessToken = null;
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

/* ═════════════════════════ 4. Query serialisation ════════════════════════ */

export type QueryValue =
  string | number | boolean | null | undefined | readonly (string | number)[];
export type QueryParams = Record<string, QueryValue>;

/**
 * Arrays repeat the key (`?statuses=A&statuses=B`) — the API sets
 * `query parser: extended`, so that is what it expects. `null`/`undefined` are
 * dropped entirely: "absent" means "leave unfiltered".
 */
export function toQueryString(params?: QueryParams): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') continue;
    if (Array.isArray(value)) {
      for (const v of value) sp.append(key, String(v));
    } else {
      sp.append(key, String(value));
    }
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

/* ════════════════════════════ 5. The request ═════════════════════════════ */

export interface RequestOptions {
  query?: QueryParams;
  /** JSON body. Money stays a string all the way down — never stringify a number. */
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** §1.9 — required on run execute/lock/reopen, exports, disbursement, baselines. */
  idempotencyKey?: string;
  /** §1.8b — optimistic concurrency on payroll runs and configuration rows. */
  ifMatch?: string;
  /** Auth endpoints and /health opt out of the bearer header and the 401 retry. */
  skipAuth?: boolean;
}

const AUTH_FREE_PATHS = new Set(['/auth/login', '/auth/refresh', '/health']);

async function parseError(res: Response, path: string): Promise<ApiError> {
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = undefined;
  }
  const envelope = (body as { error?: ApiErrorBody } | undefined)?.error;
  if (envelope && typeof envelope.code === 'string') {
    return new ApiError(envelope);
  }
  /* The contract says every non-2xx carries the envelope. If one ever does
   * not (a proxy 502, say) synthesise it rather than let `undefined` escape. */
  return new ApiError({
    code: res.status >= 500 ? 'E_INTERNAL' : 'E_NOT_FOUND',
    message: res.statusText || 'The request failed.',
    status: res.status,
    requestId: res.headers.get('X-Request-Id') ?? '',
    timestamp: new Date().toISOString(),
    path,
  });
}

async function rawRequest(
  method: string,
  path: string,
  options: RequestOptions,
): Promise<Response> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...options.headers,
  };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json; charset=utf-8';
  if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey;
  if (options.ifMatch) headers['If-Match'] = options.ifMatch;
  if (!options.skipAuth && accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  // Never without `Authorization`: an unauthenticated company assertion is
  // meaningless, and sending one would invite the server to trust it.
  if (!options.skipAuth && accessToken && companyId !== null) {
    headers[COMPANY_HEADER] = companyId;
  }

  const init: RequestInit = {
    method,
    headers,
    credentials: 'include',
  };
  if (options.body !== undefined) init.body = JSON.stringify(options.body);
  if (options.signal) init.signal = options.signal;

  try {
    return await fetch(`${API_BASE}${path}${toQueryString(options.query)}`, init);
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new NetworkError(cause);
  }
}

/**
 * One request, one retry. On a 401 the client refreshes ONCE and replays; if
 * the refresh fails it clears the token, fires the unauthorized handler
 * (which redirects to /login) and throws the original error.
 *
 * ── NOT every 401 is a dead session ─────────────────────────────────────────
 * A5 `POST /auth/change-password` answers `401 E_INVALID_CREDENTIALS` when the
 * `currentPassword` in the BODY is wrong (12-api-contract.md §3.2 A5). The
 * bearer token is perfectly good in that case, so running the recovery path
 * would refresh, replay, fail identically, then clear the token and bounce a
 * signed-in user to /login for a typo. The session codes are distinct —
 * `E_TOKEN_EXPIRED` / `E_TOKEN_INVALID` / `E_TOKEN_MISSING` (§2.2) — so the
 * credential refusal is told apart by `code` and rethrown untouched, for the
 * screen to render inline.
 */
async function request<T>(method: string, path: string, options: RequestOptions = {}): Promise<T> {
  const skipAuth = options.skipAuth ?? AUTH_FREE_PATHS.has(path);
  let res = await rawRequest(method, path, { ...options, skipAuth });

  if (res.status === 401 && !skipAuth) {
    /* Parsed BEFORE the refresh, because the body says which kind of 401 it
     * is — and because a failed refresh should still report this error, not
     * the replay's. */
    const first = await parseError(res, path);
    if (first.code === 'E_INVALID_CREDENTIALS') throw first;

    const refreshed = await refreshAccessToken();
    if (refreshed) {
      res = await rawRequest(method, path, { ...options, skipAuth });
    }
    if (!refreshed || res.status === 401) {
      accessToken = null;
      onUnauthorized(first);
      throw refreshed ? await parseError(res, path) : first;
    }
  }

  if (!res.ok) throw await parseError(res, path);
  if (res.status === 204 || res.headers.get('Content-Length') === '0') {
    return undefined as T;
  }
  const contentType = res.headers.get('Content-Type') ?? '';
  if (!contentType.includes('application/json')) {
    return (await res.text()) as unknown as T;
  }
  return (await res.json()) as T;
}

/** A resource plus the strong ETag needed for a later `If-Match` (§1.8b). */
export interface WithETag<T> {
  data: T;
  etag: string | null;
}

/**
 * The one non-JSON request shape in the app: `multipart/form-data` with a single `file` part.
 *
 * It cannot go through `request()` — that always stringifies the body and sets a JSON content
 * type — so it is a bare `fetch` that reuses this module's token getter and base path rather
 * than duplicating either. `Content-Type` is deliberately NOT set, so the browser writes the
 * multipart boundary itself; setting it by hand omits the boundary and the server sees an empty
 * body.
 *
 * Errors come back as the §1.7 envelope and are re-thrown as `ApiError`, so a caller branches on
 * `error.code` exactly as it would for any other request. It does NOT single-flight a 401 →
 * refresh → replay the way `request()` does: a `File` is consumed by the first `FormData`, and
 * replaying an upload silently would risk storing the same scan twice.
 */
export async function uploadFile<T>(path: string, file: File): Promise<T> {
  const body = new FormData();
  body.append('file', file);

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  /*
   * The company header, on the same terms `rawRequest` sets it: never without
   * `Authorization`, because an unauthenticated company assertion is meaningless.
   *
   * Omitting it here was a real bug and not a cosmetic one. `CompanyGuard` falls back to the
   * user's DEFAULT company, so a multi-company user working anywhere else would create the row
   * through `api.post` in the active company and then upload its file against the default one —
   * a 404 returned AFTER the blob had already been written to disk. It applies to D10's document
   * scans too, which is what this helper was extracted from.
   */
  if (accessToken && companyId !== null) headers[COMPANY_HEADER] = companyId;

  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    body,
    credentials: 'include',
    headers,
  });
  if (!res.ok) throw await parseError(res, path);
  // 25 MB of scan in, one JSON DTO out — every upload route returns the row it wrote.
  return (await res.json()) as T;
}

/**
 * Fetches an authenticated binary route as a `Blob` — W37 `GET /workers/{id}/photo`
 * (ADR-0015) and J44 / J46, the job reports' PDF and XLSX (ADR-0019, which pass
 * `query` and their own `accept`). Headers go through `rawRequest`, so the
 * bearer token and `X-Company-Id` are attached on the EXACT same terms as
 * every JSON request (`COMPANY_HEADER`'s own comment explains why the header
 * is never sent without a token) — unlike `uploadFile` below, which cannot
 * reuse `rawRequest` because a multipart body must NOT get a hand-set
 * `Content-Type`, but a plain GET has no such conflict.
 *
 * Not `request()`: that function decodes a non-JSON body with `res.text()`,
 * which would corrupt binary image bytes into mojibake. Not
 * `payroll/-lib/download.ts`'s `downloadBlob` either — that helper omits the
 * company header (its two callers resolve the company from the run/export id
 * server-side) and hands the blob straight to `<a download>`; this caller
 * wants the `Blob` itself, to hold in the React-Query cache and show inline
 * as an object URL (`WorkerAvatar.tsx`).
 *
 * DOES retry once on 401, mirroring `request()` — a GET is safe to replay
 * (unlike `uploadFile`'s `File`, which a first attempt already consumed), so
 * a photo fetched right after a token expiry does not need the caller to
 * retry it by hand.
 */
export async function fetchBlob(
  path: string,
  { query, accept = 'image/*' }: { query?: QueryParams; accept?: string } = {},
): Promise<Blob> {
  // `rawRequest` defaults `Accept` to JSON; say what this route actually returns, and keep
  // JSON acceptable because a refusal still arrives as the §1.7 error envelope.
  const options: RequestOptions = { query, headers: { Accept: `${accept}, application/json` } };
  let res = await rawRequest('GET', path, options);

  if (res.status === 401) {
    const first = await parseError(res, path);
    if (first.code === 'E_INVALID_CREDENTIALS') throw first;

    const refreshed = await refreshAccessToken();
    if (refreshed) res = await rawRequest('GET', path, options);
    if (!refreshed || res.status === 401) {
      accessToken = null;
      onUnauthorized(first);
      throw refreshed ? await parseError(res, path) : first;
    }
  }

  if (!res.ok) throw await parseError(res, path);
  return res.blob();
}

export const api = {
  get: <T>(path: string, query?: QueryParams, options?: Omit<RequestOptions, 'query' | 'body'>) =>
    request<T>('GET', path, { ...options, query }),

  /** GET that also surfaces the ETag, for screens that will later send If-Match. */
  getWithETag: async <T>(path: string, query?: QueryParams): Promise<WithETag<T>> => {
    const res = await rawRequest('GET', path, { query });
    if (!res.ok) throw await parseError(res, path);
    return { data: (await res.json()) as T, etag: res.headers.get('ETag') };
  },

  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'body'>) =>
    request<T>('POST', path, { ...options, body }),

  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'body'>) =>
    request<T>('PATCH', path, { ...options, body }),

  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'body'>) =>
    request<T>('PUT', path, { ...options, body }),

  del: <T = void>(path: string, options?: RequestOptions) =>
    request<T>('DELETE', path, options ?? {}),
};
